import { ApplicationConfig, APP_INITIALIZER, importProvidersFrom } from '@angular/core';
import { provideRouter, TitleStrategy } from '@angular/router';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';
import { Directionality } from '@angular/cdk/bidi';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient } from '@angular/common/http';
import { MSAL_INSTANCE, MsalService } from '@azure/msal-angular';
import { PublicClientApplication, IPublicClientApplication, BrowserCacheLocation } from '@azure/msal-browser';
import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { viewAsInterceptor } from './core/interceptors/view-as.interceptor';
import { areaPermissionInterceptor } from './core/interceptors/area-permission.interceptor';
import { storeB2cRedirectError, clearB2cRedirectError } from './core/auth/b2c-redirect-error';
import { isPasswordResetPolicyResult } from './core/auth/b2c-password-reset';
import { restoreReturnUrl } from './core/auth/return-url';
import { PageTitleStrategy } from './core/title/page-title.strategy';
import { AppDirectionality } from './core/i18n/app-directionality';
import { environment } from '../environments/environment';

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

/** Single Azure AD B2C client for the whole app (redirect flow). */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.clientId,
      authority: environment.msalConfig.authority,
      knownAuthorities: environment.msalConfig.knownAuthorities,
      redirectUri: environment.msalConfig.redirectUri,
      postLogoutRedirectUri: environment.msalConfig.postLogoutRedirectUri,
      navigateToLoginRequestUrl: false,
    },
    cache: { cacheLocation: BrowserCacheLocation.LocalStorage },
  });
}

/** Initialize MSAL and process the B2C redirect BEFORE the router/guards run, so
 *  getActiveAccount() (read by hotelManagerGuard) is already correct on first navigation.
 *
 *  `resetAuthority` is a parameter only so tests can exercise the password-reset branch; the
 *  APP_INITIALIZER factory passes just the instance and gets the environment's value. */
export function initializeMsal(
  instance: IPublicClientApplication,
  resetAuthority: string | undefined = environment.msalConfig.passwordResetAuthority,
): () => Promise<void> {
  return async () => {
    await instance.initialize();
    try {
      const result = await instance.handleRedirectPromise();
      if (result?.account && isPasswordResetPolicyResult(result, resetAuthority)) {
        // The user just completed the dedicated password-reset policy (login.component.ts
        // relaunches MSAL against it on AADB2C90118). Its tokens must NOT become the session:
        // MSAL caches B2C credentials per policy (realm = tfp), so the refresh token it just got
        // can never be renewed against the sign-in policy (AADB2C90088 from that token endpoint)
        // and the session would die within the hour on an "Access denied" page -- nor are the
        // reset policy's claims guaranteed to carry the `emails` the BE resolves the role from.
        // Drop everything MSAL cached, including any pre-reset account (B2C revoked its refresh
        // token with the password change, so adopting it below would fail the same way), and
        // stay signed out: the guard on the landing route then calls loginRedirect() against the
        // sign-in policy as usual, where B2C's SSO session lets the user straight in -- the
        // documented B2C pattern for a separate reset policy ("reset done, now sign in").
        console.info('[MSAL] password-reset policy completed; re-entering the sign-in policy');
        await instance.clearCache();
        clearB2cRedirectError();
      } else if (result?.account) {
        instance.setActiveAccount(result.account);
        clearB2cRedirectError();
        // B2C always answers on the site root: resume on the page the sign-in started from (a
        // deep link, a reload after the session expired) instead of the role's home (WI 3861).
        // Done here, before the router's first navigation reads the address bar.
        restoreReturnUrl();
      }
    } catch (e) {
      // A failed/cancelled redirect must not brick app bootstrap — stay unauthenticated. Record
      // the error (see b2c-redirect-error.ts) instead of only logging it, so the login page can
      // show it — otherwise every guard just sees "no account" and calls loginRedirect() again,
      // silently restarting the same B2C policy (e.g. after the user clicks "Forgot your
      // password?", which comes back as an error here, not a normal result).
      console.error('MSAL redirect handling failed', e);
      storeB2cRedirectError(e);
    }
    if (!instance.getActiveAccount()) {
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) instance.setActiveAccount(accounts[0]);
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    // The browser tab title follows the page and the UI language (WI 3787) — see app.routes.ts.
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    // Order matters: the bearer token first, then (F7/F9) the admin view-as group header, then
    // (Feature 4123) the page-area refusals (403 AREA_PERMISSION_DENIED).
    provideHttpClient(withXhr(), withInterceptors([authInterceptor, viewAsInterceptor, areaPermissionInterceptor]), withInterceptorsFromDi()),
    provideAnimationsAsync(),
    { provide: MAT_ICON_DEFAULT_OPTIONS, useValue: { fontSet: 'material-symbols-outlined' } },
    // Material/CDK follow the UI language's direction at runtime (Arabic → rtl, WI 3974): the
    // CDK's own root Directionality reads <html dir> once and never updates — see AppDirectionality.
    { provide: Directionality, useExisting: AppDirectionality },
    // Azure AD B2C (MSAL) — login/redirect, and (Milestone 2) the real access token via authInterceptor.
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: APP_INITIALIZER, useFactory: initializeMsal, deps: [MSAL_INSTANCE], multi: true },
    MsalService,
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        useDefaultLang: true,
      })
    ),
  ],
};
